/**
 * financialSSOTAdapter.js
 *
 * Phase 2 — Financial SSOT Adapter. `cost_performance` dataset only.
 *
 * The ONLY thing this module does: call buildCumulativeData() and map
 * its output fields onto the names the Analytical Query Engine expects.
 * No branching on financial meaning, no recomputation, no fallback
 * arithmetic. If a mapped field is missing on the SSOT's output, that is
 * surfaced as a gap to fix in buildCumulativeData() itself — never
 * patched here.
 *
 * Architecture being enforced (per Wessam's explicit instruction):
 *   User Question → AnalyticalQuery → SSOT Adapter → Existing Domain
 *   Owner → Analytical Result → Visualization
 * NOT:
 *   Analytics Studio → queries tables → recomputes Budget/Actual/EAC
 */

import { buildCumulativeData } from './buildCostControlData'
import { validateQuery } from './analyticalQuery'

// ─── Multi-select Filter Contract (approved, applied uniformly across
// every filter key in this Adapter) ─────────────────────────────────────
//   string    → single-value equality (backward compatible with every
//               existing filter usage before this Extension)
//   string[]  → OR-selection (row matches if its value is in the array)
//   'all' / undefined / null → no filter (row always passes)
// Applied identically whether the row's own field is compared directly
// (cost_type, indirect_category) or via child existence (resource_type,
// sub_code, resource_code) — this function only decides "does this ONE
// candidate value satisfy the filter", the caller decides how to obtain
// the candidate value(s) from a row.
function matchesFilter(rowValue, filterValue) {
  if (filterValue == null || filterValue === 'all') return true
  if (Array.isArray(filterValue)) return filterValue.includes(rowValue)
  return rowValue === filterValue
}

// Existence-style filter: true if ANY candidate value (e.g. every
// Resource's resource_type under a Cost Code) satisfies matchesFilter.
function matchesAnyFilter(candidateValues, filterValue) {
  if (filterValue == null || filterValue === 'all') return true
  return candidateValues.some(v => matchesFilter(v, filterValue))
}

// ─── Project-level measure mapping ─────────────────────────────────────────
// Every entry here is a direct field read off buildCumulativeData()'s
// output — no arithmetic beyond what buildCumulativeData() itself
// already computed (variance is the sole exception, and it uses the
// exact formula documented in the Comparison Engine design: budget − eac,
// composed from two already-computed SSOT fields, not re-derived from
// raw data).
function mapProjectLevelMeasure(measureKey, snapshot) {
  const { grandTotals, meta } = snapshot
  switch (measureKey) {
    case 'budget':        return grandTotals.bac
    case 'actual':         return grandTotals.actualTotal
    case 'eac':             return grandTotals.eac
    case 'commitment':       return grandTotals.commitment
    case 'contractValue':     return meta.contractValue
    case 'grossProfit':        return meta.grossProfit
    case 'budgetProfit':        return meta.budgetProfit
    case 'variance':             return grandTotals.bac - grandTotals.eac
    case 'cpi':
      // Confirmed present on grandTotals — calcTotal() in
      // buildCostControlData.js (line 814-826) computes .cpi at every
      // level it's applied to (directTotals, indirectTotals,
      // grandTotals = calcTotal([directTotals, indirectTotals])), as
      // Σ(earnedValue) ÷ Σ(totalActualCost) — never averaged or summed
      // from child CPI values. Earlier revision of this adapter
      // incorrectly assumed grandTotals.cpi did not exist; corrected
      // after direct verification against the SSOT source.
      return grandTotals.cpi
    default:
      throw new Error(`Unmapped measure: "${measureKey}"`)
  }
}

// ─── Row-level measure mapping (used when a dimension is requested) ───────
function mapRowLevelMeasure(measureKey, row) {
  switch (measureKey) {
    case 'budget':      return row.bac || 0
    case 'actual':       return row.totalActualCost || 0
    case 'eac':           return row.eac || 0
    case 'commitment':     return row.commitment || 0
    case 'cpi':             return row.cpi || 0
    case 'variance':         return (row.bac || 0) - (row.eac || 0)
    // contractValue / grossProfit / budgetProfit are project-level
    // metadata, not per-Cost-Code fields — requesting them with a
    // dimension is a query-shape error.
    case 'contractValue':
    case 'grossProfit':
    case 'budgetProfit':
      throw new Error(
        `'${measureKey}' is project-level metadata and has no per-row ` +
        `value — query it with dimension: null instead.`
      )
    default:
      throw new Error(`Unmapped measure: "${measureKey}"`)
  }
}

// ─── Dimension grouping ─────────────────────────────────────────────────────
// FILTER CONTRACT (Phase 6A, approved):
//   cost_type          → direct row equality (r.cost_type === value)
//   indirect_category   → indirect_category_id equality (r.indirect_category_id === value)
//   resource_type        → descendant-resource EXISTENCE
//                          (r.resourceRows.some(res => res.resource_type === value))
//
// All three are MEMBERSHIP filters, not measure filters: they decide
// which Cost Code rows are INCLUDED, never which portion of a Cost
// Code's value counts. A Cost Code with both Material and Labour
// Resources, filtered by resource_type='material', still shows its FULL
// Actual/Budget/EAC/Commitment (both Resources' combined total) — never
// re-aggregated down to just the Material Resource's contribution. This
// is the explicit v1 decision: resource_type filtering answers "does
// this Cost Code contain at least one Material Resource?", not "what
// portion of this Cost Code is Material?". No new financial calculation,
// no reclassification — pre-aggregation row selection only, same
// posture as the existing cost_type filter.
function getDimensionRows(snapshot, dimension, filters) {
  const { directRows, indirectRows } = snapshot
  let rows = [...(directRows || []), ...(indirectRows || [])]

  if (filters?.cost_code != null) {
    // Direct row equality — r.code IS the Cost Code's own identifier,
    // no existence check or hierarchy lookup needed, the simplest of
    // all filter keys. Lets a user narrow ANY dimension (resource_type,
    // sub_code, etc.) down to one or more specific Cost Codes, not just
    // the reverse (drilling from a dimension value down to Cost Codes).
    rows = rows.filter(r => matchesFilter(r.code, filters.cost_code))
  }

  if (filters?.cost_type != null) {
    rows = rows.filter(r => matchesFilter(r.cost_type, filters.cost_type))
  }

  if (filters?.indirect_category != null) {
    // indirect_category_id lives directly on the Cost Code row (r) —
    // confirmed by direct inspection of buildCumulativeData()'s own
    // indirectCategories construction, which filters indirectRows the
    // same way (indirectRows.filter(r => r.indirect_category_id ===
    // cat.id)). Direct rows always have indirect_category_id undefined/
    // null and are naturally excluded — no separate Direct/Indirect
    // branch needed here, unlike the historicalSSOTAdapter.js
    // investigation where no such per-row field existed at all.
    rows = rows.filter(r => matchesFilter(r.indirect_category_id, filters.indirect_category))
  }

  if (filters?.resource_type != null) {
    // Existence filter, not equality — resource_type is a Resource-level
    // field (res.resource_type), never present on the Cost Code row (r)
    // itself. A Cost Code is included if ANY of its Resources match
    // (single value or any value in an array — matchesAnyFilter handles both).
    rows = rows.filter(r =>
      matchesAnyFilter((r.resourceRows || []).map(res => res.resource_type), filters.resource_type)
    )
  }

  if (filters?.sub_code != null) {
    // Existence filter over Sub Code children. Confirmed (Final Gate,
    // this Stage) that buildCumulativeData()'s children array on each
    // Cost Code root — sourced from subRows (buildCostControlData.js,
    // ~line 680) — carries real per-Sub-Code values (bac/totalActualCost/
    // eac/commitment/cpi via the {...sub, ...} spread), NOT just hierarchy
    // metadata. A Cost Code is included if ANY of its Sub Codes' code
    // matches. This is membership filtering only — the Cost Code's FULL
    // totals are still shown, never re-aggregated down to just the
    // matching Sub Code (same posture as resource_type above).
    rows = rows.filter(r =>
      matchesAnyFilter((r.children || []).map(c => c.code), filters.sub_code)
    )
  }

  if (filters?.resource_code != null) {
    // Existence filter over Resource Code — resourceRows[].code (the
    // Resource's own code, distinct from resource_type which is its
    // category). Same membership-only posture.
    rows = rows.filter(r =>
      matchesAnyFilter((r.resourceRows || []).map(res => res.code), filters.resource_code)
    )
  }

  if (dimension === 'cost_code') {
    // One entry per row already IS per-Cost-Code — no grouping needed,
    // buildCumulativeData()'s directRows/indirectRows are already at
    // Cost Code grain.
    return rows.map(r => ({ key: r.code, label: r.description || r.code, row: r }))
  }

  if (dimension === 'cost_type') {
    const groups = { direct: [], indirect: [] }
    rows.forEach(r => { (groups[r.cost_type] || (groups[r.cost_type] = [])).push(r) })
    return Object.entries(groups).map(([key, groupRows]) => ({
      key, label: key, row: aggregateRows(groupRows),
    }))
  }

  if (dimension === 'package') {
    // Per the Baseline Doc's documented convention: "Package = Cost Code
    // description" in this project — grouping by description IS
    // grouping by package. No separate package_name field is invented.
    const groups = {}
    rows.forEach(r => {
      const key = r.description || r.code
      ;(groups[key] || (groups[key] = [])).push(r)
    })
    return Object.entries(groups).map(([key, groupRows]) => ({
      key, label: key, row: aggregateRows(groupRows),
    }))
  }

  if (dimension === 'sub_code') {
    // Flat: every Sub Code across every included Cost Code, one row
    // each — NOT grouped/summed under their parent. Confirmed (Final
    // Gate, this Stage) that each child in r.children already carries
    // real, independently-computed values (bac/totalActualCost/eac/
    // commitment/cpi/earnedValue/varianceEac/varianceCompletion via the
    // {...sub, ...enriched} spread in buildCostControlData.js's subRows
    // construction, ~line 727) — this is a direct read, not a
    // re-aggregation of anything.
    //
    // Field name correction (discovered after the initial Stage 1
    // implementation shipped 'undefined' labels): subRows' final
    // shape is `{ ...sub, ...enriched, isChild: true }`, where `sub`
    // comes directly from the cost_codes table's own
    // `select('id, code, description, ...')` — the field is `code`
    // (and `description`), NOT `rowKey`. `rowKey` only exists on a
    // DIFFERENT, unrelated object — resourceOwnershipNode.children,
    // built earlier in buildCostControlData.js purely as an internal
    // input to resolveForecastOwnership(), never returned to any
    // caller. Confusing the two objects (both temptingly named
    // "children" in different parts of the same file) was the actual
    // root cause of every Sub Code row displaying as "undefined".
    const allSubCodes = rows.flatMap(r =>
      (r.children || []).map(c => ({ ...c, parentCode: r.code, parentDescription: r.description }))
    )
    return allSubCodes.map(c => ({
      key: c.code,
      label: `${c.code} (${c.parentDescription || c.parentCode})`,
      row: c,
    }))
  }

  if (dimension === 'resource_code') {
    // Flat: every Resource across every included Cost Code (Direct
    // rows only — resourceRows exists only on directRows per
    // buildCumulativeData()'s own construction). Same direct-read
    // posture as sub_code above — resourceRows entries already carry
    // real values (bac/totalActualCost/eac/commitment/cpi/earnedValue),
    // confirmed in the same Stage 1 investigation used for financial
    // extension of the resource_type dimension.
    const allResources = rows.flatMap(r =>
      (r.resourceRows || []).map(res => ({ ...res, parentCode: r.code, parentDescription: r.description }))
    )
    return allResources.map(res => ({
      key: res.code,
      label: `${res.code} (${res.parentDescription || res.parentCode})`,
      row: res,
    }))
  }

  // resource_type and indirect_category are handled entirely separately
  // from the branches above — they do NOT come from directRows/
  // indirectRows grouped by this Adapter. They read PRE-AGGREGATED
  // objects buildCumulativeData() already builds and returns
  // (resourceTypeTotals, indirectCategoriesWithTotals,
  // uncategorizedTotals) — zero re-summing here, same posture as every
  // other Adapter's use of an Owner's own aggregation. See
  // getResourceTypeRows()/getIndirectCategoryRows() below.
  if (dimension === 'resource_type' || dimension === 'indirect_category') {
    throw new Error(
      `${dimension} is handled by a dedicated function, not ` +
      `getDimensionRows() — this branch should be unreachable.`
    )
  }

  throw new Error(`Unsupported dimension: "${dimension}"`)
}

// ─── resource_type dimension (Direct only) ─────────────────────────────────
// Reads buildCumulativeData()'s resourceTypeTotals VERBATIM — a
// pre-aggregated object with exactly 4 fixed keys (material, labour,
// subcontract, equipment). Confirmed by direct inspection of the Owner
// (buildCostControlData.js, line 895): a Resource with no matching
// resource_type is silently excluded from ALL four totals — there is no
// 'Unclassified' bucket here. This is DELIBERATELY different from
// monthly_actual_cost's resource_type (which has 5 values including
// 'Unclassified', via a completely separate Owner —
// monthlyActualCostHierarchy.js's resolveResourceType(), which never
// drops a row). This Adapter does not invent an 'Unclassified' entry to
// paper over that difference — the two datasets' resource_type values
// are NOT the same categorization and are not forced to match.
function getResourceTypeRows(snapshot) {
  const { resourceTypeTotals } = snapshot
  return Object.entries(resourceTypeTotals).map(([type, totals]) => ({
    key: type,
    label: type,
    row: totals,
  }))
}

// ─── indirect_category dimension (Indirect only) ───────────────────────────
// Reads buildCumulativeData()'s indirectCategoriesWithTotals +
// uncategorizedTotals VERBATIM. Unlike resourceTypeTotals,
// uncategorizedTotals IS a distinct pre-aggregated bucket the Owner
// already computes (buildCostControlData.js) — included here as its own
// row labeled 'Uncategorized', since the Owner itself treats it as a
// first-class total, not an omission. Categories come entirely from
// whatever indirect_categories rows exist for this project — no fixed/
// invented list.
function getIndirectCategoryRows(snapshot) {
  const { indirectCategories, uncategorizedTotals } = snapshot
  const rows = indirectCategories.map(cat => ({
    key: cat.name,
    label: cat.name,
    row: cat.totals,
  }))
  // Only included if the Owner's own uncategorizedTotals reflects actual
  // rows — an empty/zero uncategorized bucket still appears (same
  // "allowed, not hidden" posture used throughout this system), since
  // hiding it based on a value check would be this Adapter making a
  // financial judgment call the Owner itself doesn't make.
  rows.push({ key: 'Uncategorized', label: 'Uncategorized', row: uncategorizedTotals })
  return rows
}

// Sums the additive fields across a group of rows for cost_type/package
// grouping, then derives cpi from the SAME rolled-up earnedValue/
// totalActualCost — identical to calcTotal()'s own approach in
// buildCostControlData.js (never summed/averaged from child cpi values).
// Note: buildCumulativeData()'s row objects don't expose earnedValue on
// direct/indirect Cost Code rows directly in all cases the way resource
// rows do — if earnedValue is unavailable on a given row shape, it
// contributes 0, matching calcTotal()'s own (acc.earnedValue && ...)
// guard rather than fabricating a value.
function aggregateRows(rows) {
  const earnedValue = rows.reduce((s, r) => s + (r.earnedValue || 0), 0)
  const totalActualCost = rows.reduce((s, r) => s + (r.totalActualCost || 0), 0)
  return {
    bac: rows.reduce((s, r) => s + (r.bac || 0), 0),
    totalActualCost,
    eac: rows.reduce((s, r) => s + (r.eac || 0), 0),
    commitment: rows.reduce((s, r) => s + (r.commitment || 0), 0),
    cpi: (earnedValue && totalActualCost) ? earnedValue / totalActualCost : 0,
  }
}

/**
 * Executes an AnalyticalQuery against the cost_performance dataset.
 * The ONLY dataset wired up in this phase — throws for any other
 * dataset value, since no other Adapter mapping exists yet.
 */
export async function executeQuery(projectId, query) {
  const { valid, errors } = validateQuery(query)
  if (!valid) throw new Error(`Invalid query: ${errors.join('; ')}`)
  if (query.dataset !== 'cost_performance') {
    throw new Error(
      `Dataset "${query.dataset}" has no SSOT Adapter mapping yet — ` +
      `only "cost_performance" is wired up in this phase.`
    )
  }

  const now = new Date()
  const snapshot = await buildCumulativeData(projectId, now.getMonth() + 1, now.getFullYear())

  // ── No dimension: single project-level result row ──────────────────────
  if (!query.dimension) {
    const values = {}
    for (const m of query.measures) {
      values[m] = mapProjectLevelMeasure(m, snapshot)
    }
    if (query.comparison?.type === 'variance') {
      const baseline = mapProjectLevelMeasure(query.comparison.baseline, snapshot)
      for (const m of query.measures) {
        if (m === query.comparison.baseline) continue
        values[`${m}_vs_${query.comparison.baseline}`] = values[m] - baseline
      }
    }
    return { grain: 'project', rows: [{ key: projectId, label: 'Project', values }] }
  }

  // ── Dimension requested: one result row per dimension key ──────────────
  // resource_type/indirect_category read pre-aggregated Owner objects
  // (dedicated functions below); cost_code/cost_type/package/sub_code/
  // resource_code all flow through getDimensionRows() uniformly, since
  // each is a direct read/grouping over directRows/indirectRows or their
  // children/resourceRows — no pre-aggregated Owner object exists for
  // any of them.
  const dimensionRows = (query.dimension === 'resource_type')
    ? getResourceTypeRows(snapshot)
    : (query.dimension === 'indirect_category')
      ? getIndirectCategoryRows(snapshot)
      : getDimensionRows(snapshot, query.dimension, query.filters)

  const resultRows = dimensionRows.map(({ key, label, row }) => {
    const values = {}
    for (const m of query.measures) {
      values[m] = mapRowLevelMeasure(m, row)
    }
    if (query.comparison?.type === 'variance') {
      const baseline = mapRowLevelMeasure(query.comparison.baseline, row)
      for (const m of query.measures) {
        if (m === query.comparison.baseline) continue
        values[`${m}_vs_${query.comparison.baseline}`] = values[m] - baseline
      }
    }
    return { key, label, values }
  })

  return { grain: query.dimension, rows: resultRows }
}
